

'use client';

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardTitle, CardHeader } from '@/components/ui/card';
import Link from 'next/link';
import Image from 'next/image';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { ChevronDown, Clock, ParkingCircle, ArrowRight, Lightbulb, Loader, Search, ArrowUp, Info, Star, WifiOff } from 'lucide-react';
import placeholderImages from '@/lib/placeholder-images.json';
import { type Salon } from '@/app/lib/salons';
import { slugify } from '@/lib/utils';
import DaisyconAd from './DaisyconAd';
import { daisyconAds } from '@/app/lib/ads';
import PersonalAd from './PersonalAd';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import salonTips from '@/app/lib/tips.json';
import { useCollection, useFirestore, useMemoFirebase } from '@/firebase';
import { collection, query, where, Timestamp } from 'firebase/firestore';
import { type Review } from '@/models/review';
import { revalidateSalons } from '@/app/lib/actions';


  type SalonWithDistance = Salon & { distance?: number };

  const infoBlocks = [
    {
      type: 'info-block',
      id: 'info-block-1',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Waarom een hondentrimsalon belangrijk is</h3>
          <div className="space-y-2">
            <p>Een hondentrimsalon is geen luxe, maar in veel gevallen een essentieel onderdeel van het welzijn van je hond. Afhankelijk van het ras, het vachttype en de levensfase, kan regelmatig trimmen huidproblemen voorkomen, ongemak verminderen en bijdragen aan een gezonde vacht.</p>
            <p>Tijdens een trimbeurt wordt de vacht gecontroleerd op klitten, parasieten en huidirritaties. Ook nagels, oren en ogen worden nagekeken. Voor bepaalde rassen, zoals die met een krullende of constant doorgroeiende vacht, is professionele verzorging zelfs noodzakelijk om pijnlijke klitten en huidontstekingen te voorkomen.</p>
          </div>
        </>
      ),
    },
    {
      type: 'info-block',
      id: 'info-block-2',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Het verloop van een bezoek</h3>
          <div className="space-y-2">
            <p>Een standaardbezoek start al thuis. Een wandeling vooraf helpt de hond om rustig aan te komen. In het salon neemt de trimmer de tijd om kennis te maken, wat zeker bij angstige honden belangrijk is.</p>
            <p>De trimbeurt zelf omvat vaak wassen, drogen, knippen of scheren, en de verzorging van nagels en oren. Een professionele trimmer werkt met pauzes en past zich aan het comfort van de hond aan. Na afloop is de hond vaak moe, maar proper en comfortabel.</p>
          </div>
        </>
      ),
    },
    {
      type: 'info-block',
      id: 'info-block-3',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Hoe kies je het juiste trimsalon?</h3>
          <div className="space-y-2">
           <p>Niet elk trimsalon is hetzelfde. Sommige zijn gespecialiseerd in specifieke rassen, andere richten zich op angstige honden. Let op ervaring, hygiëne en de aanpak. Goede communicatie is cruciaal: een professionele trimmer stelt vragen over je hond en geeft eerlijk advies.</p>
            <p>Elke hond is anders en een kwalitatief salon houdt hier rekening mee. Steeds meer salons bieden alternatieven zoals mobiele diensten of één-op-één afspraken, wat voor sommige honden een groot verschil in stress kan maken.</p>
          </div>
        </>
      ),
    },
  ];

  const shortArticles = [
    {
      type: 'info-block',
      id: 'info-block-puppy',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Wanneer met een puppy voor het eerst?</h3>
          <div className="space-y-2">
            <p>Wacht niet tot je puppy zes maanden oud is! De ideale tijd voor een eerste kennismaking is zodra de volledige inentingen gegeven zijn, meestal rond de 12 tot 15 weken. Deze eerste beurt draait niet om een perfect kapsel, maar om gewenning. Een goede trimmer laat je pup spelenderwijs kennismaken met de tafel, de geluiden van de schaar en het bad. Jong geleerd is oud gedaan: een pup die vroeg went, is voor de rest van zijn leven een ontspannen klant in het salon. Zoek hier een trimmer die gespecialiseerd is in puppy-gewenning.</p>
          </div>
        </>
      ),
    },
    {
      type: 'info-block',
      id: 'info-block-frequency',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Hoe vaak moet mijn hond naar het trimsalon?</h3>
          <div className="space-y-2">
            <p>Dit is de meest gestelde vraag. Het antwoord hangt af van de vacht, maar een goede vuistregel voor rassen met doorgroeiende vachten (zoals Doodles, Maltezers en Poeidels) is elke 8 tot 10 weken. Voor ruwharige honden (plukvachten) geldt vaak elke 3 tot 4 maanden. Wacht je langer? Dan is de kans op viltvorming groot, wat pijnlijk is voor de hond en de behandeling duurder maakt. Zie het trimsalon niet als een noodoplossing, maar als periodiek onderhoud voor een gezonde huid en vacht.</p>
          </div>
        </>
      ),
    },
    {
      type: 'info-block',
      id: 'info-block-winter',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Waarom vachtverzorging in de winter essentieel is</h3>
          <div className="space-y-2">
            <p>Veel eigenaren laten de vacht in de winter lang groeien "voor de warmte". Dit is een hardnekkig misverstand dat vaak averechts werkt. Een te lange vacht houdt vocht en modder vast, waardoor de hond juist koud en klam blijft na een wandeling. Bovendien droogt het langzaam, wat de perfecte basis is voor klitten en vilt. Een goed verzorgde, klitvrije vacht isoleert veel beter dan een lange, vervilte massa. Vraag je trimmer om een 'winter-model': iets langer dan in de zomer, maar wel praktisch en luchtig.</p>
          </div>
        </>
      ),
    },
    {
      type: 'info-block',
      id: 'info-block-techniques',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Plukken, knippen, effileren of scheren?</h3>
          <div className="space-y-2">
            <p>Zie je door de bomen het bos niet meer? Geen zorgen, een gediplomeerd trimmer weet precies wat jouw hond nodig heeft. Een ruwharige Teckel moet bijvoorbeeld 'geplukt' worden (dode haren worden verwijderd) om de vachtstructuur te behouden, terwijl scheren de vacht juist zou verpesten. Een zachte krulvacht wordt daarentegen geknipt. Elk ras heeft een specifieke vachtstructuur die een eigen techniek vereist. Kies in deze lijst een professional die de rasstandaard van jouw hond kent en respecteert voor het mooiste resultaat.</p>
          </div>
        </>
      ),
    },
    {
      type: 'info-block',
      id: 'info-block-price',
      content: (
        <>
          <h3 className="text-xl font-semibold mb-2">Waarom de prijs van een trimbeurt varieert</h3>
          <div className="space-y-2">
            <p>Soms schrikken mensen van de prijs, maar vergeet niet: hondentrimmen is ambachtelijk, fysiek zwaar handwerk. Een trimmer is tegelijkertijd kapper, pedicure, en soms zelfs een beetje gedragstherapeut. De prijs wordt bepaald door de grootte van de hond, de staat van de vacht (klitten kosten meer tijd) en het gedrag van de hond op tafel. Een goedkope beurt kan duur uitpakken als de vacht verkeerd behandeld wordt. Investeer in kwaliteit; je betaalt voor de expertise, de veilige materialen en de liefdevolle aandacht die jouw hond krijgt.</p>
          </div>
        </>
      ),
    },
];
  
  const InfoBlockCard = ({ content }: { content: React.ReactNode }) => (
    <Card className="bg-muted/30 border-border">
        <CardContent className="p-4 prose prose-sm dark:prose-invert max-w-none text-foreground/80">
            {content}
        </CardContent>
    </Card>
  );
  
  const TipCard = ({ tip }: { tip: string }) => (
    <Card className="bg-primary/10 border-primary/30">
      <CardContent className="p-4 flex items-start gap-4">
        <div className="flex-shrink-0">
          <Lightbulb className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-grow">
          <h3 className="font-bold text-sm">Tip</h3>
          <p className="text-muted-foreground text-sm">
            {tip}
          </p>
        </div>
      </CardContent>
    </Card>
  );

export default function SalonList({ initialSalons, allAreas, currentArea, provinceName }: { initialSalons: Salon[], allAreas?: string[], currentArea?: string, provinceName: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const firestore = useFirestore();

  const [salons, setSalons] = useState(initialSalons);
  const [isRetrying, setIsRetrying] = useState(false);
  const [reviewStats, setReviewStats] = useState<Record<string, { score: number; count: number }>>({});
  const [topAreas, setTopAreas] = useState<string[]>([]);
  const [otherAreas, setOtherAreas] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isClient, setIsClient] = useState(false);
  const [pendingAreaSlug, setPendingAreaSlug] = useState<string | null>(null);
  const [shuffledTips, setShuffledTips] = useState<string[]>([]);
  const { toast } = useToast();
  const [isLocating, setIsLocating] = useState(false);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [shuffledInfoBlocks, setShuffledInfoBlocks] = useState<(typeof infoBlocks)>([]);
  
  const provinceSlug = useMemo(() => provinceName ? slugify(provinceName) : '', [provinceName]);

  const showOtherAnimalsParam = searchParams.get('showOtherAnimals') === 'true';
  const [showOtherAnimals, setShowOtherAnimals] = useState(showOtherAnimalsParam);
  
  const fiveYearsAgo = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 5);
    return date;
  }, []);

  // Simplified query to avoid composite index, we will filter client-side.
  const reviewsQuery = useMemoFirebase(() => {
    if (!firestore || !provinceName) return null;
    return query(
      collection(firestore, 'reviews'),
      where('province', '==', provinceName)
    );
  }, [firestore, provinceName]);

  const { data: allProvincialReviews } = useCollection<Review>(reviewsQuery);

  useEffect(() => {
    if (allProvincialReviews) {
      // Perform filtering on the client to avoid complex queries requiring indexes.
      const approvedAndRecentReviews = allProvincialReviews.filter(review => {
          const createdAtDate = review.createdAt?.toDate ? review.createdAt.toDate() : new Date(0);
          return review.status === 'approved' && createdAtDate >= fiveYearsAgo;
      });

      const stats = approvedAndRecentReviews.reduce((acc, review) => {
          if (!acc[review.salonId]) {
              acc[review.salonId] = { totalScore: 0, count: 0 };
          }
          acc[review.salonId].totalScore += (review.scoreQuality + review.scoreHandling + review.scoreService) / 3;
          acc[review.salonId].count++;
          return acc;
      }, {} as Record<string, { totalScore: number; count: number }>);

      const finalStats = Object.keys(stats).reduce((acc, salonId) => {
          acc[salonId] = {
              score: stats[salonId].totalScore / stats[salonId].count,
              count: stats[salonId].count
          };
          return acc;
      }, {} as Record<string, { score: number; count: number }>);

      setReviewStats(finalStats);
    }
  }, [allProvincialReviews, fiveYearsAgo]);

  useEffect(() => {
    setShowOtherAnimals(showOtherAnimalsParam);
  }, [showOtherAnimalsParam]);

  const handleShowOtherAnimalsChange = (checked: boolean) => {
    // Update local state immediately for instant UI feedback on the switch.
    setShowOtherAnimals(checked);

    // Update the URL parameters in the background to ensure the state is
    // bookmarkable and consistent with the URL.
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (checked) {
      newSearchParams.set('showOtherAnimals', 'true');
    } else {
      newSearchParams.delete('showOtherAnimals');
    }
    // Remove location from params when toggling the switch
    newSearchParams.delete('location');
    // We use router.replace instead of router.push to avoid adding a new entry to the history stack
    // for a simple filter change.
    router.replace(`${pathname}?${newSearchParams.toString()}`);
  };


  useEffect(() => {
    setIsClient(true);
    const allInfoBlocks = [...infoBlocks, ...shortArticles];
    setShuffledInfoBlocks([...allInfoBlocks].sort(() => 0.5 - Math.random()));

    const locationParam = searchParams.get('location');
    if (locationParam) {
        setSearchTerm(locationParam);
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.delete('location');
        router.replace(`${pathname}?${newSearchParams.toString()}`, { scroll: false });
    }
  
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowScrollTopButton(currentScrollY > 400);
    };
  
    window.addEventListener('scroll', handleScroll, { passive: true });
  
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShuffledTips([...salonTips].sort(() => 0.5 - Math.random()));
  }, []);
  
  useEffect(() => {
    if (allAreas) {
      const uniqueAreas = Array.from(new Set(allAreas.map(a => a.trim()).filter(Boolean)));
      setTopAreas(uniqueAreas.slice(0, 5));
      setOtherAreas(uniqueAreas.slice(5));
    }
  }, [allAreas]);

  const normalizeText = (text: string) => {
    if (!text) return '';
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/'/g, "").toLowerCase();
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      toast({
        variant: "destructive",
        title: "Locatie niet ondersteund",
        description: "Uw browser ondersteunt geen geolocatie.",
      });
      return;
    }
    
    setIsLocating(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Use OpenStreetMap's free reverse geocoding API
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=nl`);
          const data = await response.json();
          
          // Extract the city or a relevant locality
          const city = data.address.city || data.address.town || data.address.village || data.address.suburb;
          
          if (city) {
            setSearchTerm(city);

            toast({
              title: `Locatie ingesteld op: ${city}`,
              description: "De resultaten worden gefilterd op deze gemeente.",
            });
          } else {
             toast({
              variant: "destructive",
              title: "Gemeente niet gevonden",
              description: "We konden uw gemeente niet automatisch bepalen.",
            });
          }
        } catch (error) {
           toast({
              variant: "destructive",
              title: "Fout bij ophalen locatie",
              description: "Er is iets misgegaan bij het omzetten van uw locatie.",
            });
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        setIsLocating(false);
        toast({
          variant: "destructive",
          title: "Locatie onbekend",
          description: "We konden uw locatie niet automatisch bepalen. Controleer uw browserinstellingen of probeer het later opnieuw.",
        });
      }
    );
  };


  const salonsAfterAreaAndSearch = useMemo(() => {
    let salonsToFilter: SalonWithDistance[] = [...salons];

    const activeArea = pendingAreaSlug === 'Alle' ? 'Alle' : currentArea;
    if (!searchTerm && activeArea && activeArea !== 'Alle') {
      const normalizedActiveArea = normalizeText(activeArea);
      const escapedNormalizedArea = normalizedActiveArea.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
      const areaRegex = new RegExp(`\\b${escapedNormalizedArea}\\b`, 'i');

      salonsToFilter = salonsToFilter.filter(salon => {
        const normalizedArea = normalizeText(salon.area);
        const normalizedAddress = normalizeText(salon.address || '');
        
        const isAreaMatch = normalizedArea === normalizedActiveArea;
        const isAddressMatch = areaRegex.test(normalizedAddress);

        return isAreaMatch || isAddressMatch;
      });
    }

    if (searchTerm) {
      const trimmedSearchTerm = searchTerm.trim();
      const normalizedSearch = normalizeText(trimmedSearchTerm);
      // Use a regex with word boundaries for searching in area and address fields.
      const searchRegex = new RegExp(`\\b${normalizedSearch.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i');

      salonsToFilter = salonsToFilter.filter(salon => {
        // Broad search for name and specialization
        if (normalizeText(salon.name).includes(normalizedSearch) || normalizeText(salon.extra2 || '').includes(normalizedSearch)) {
          return true;
        }

        // Strict, whole-word search for area and address
        if (searchRegex.test(normalizeText(salon.area)) || searchRegex.test(normalizeText(salon.address || ''))) {
          return true;
        }
        
        // Special case for "'t Zuid"
        if (salon.area.toLowerCase() === "t zuid" && normalizedSearch.includes("'t zuid")) {
          return true;
        }
        
        return false;
      });
    }
    return salonsToFilter;
  }, [salons, currentArea, pendingAreaSlug, searchTerm]);

  const filteredSalons = useMemo(() => {
    let salonsToFilter = [...salonsAfterAreaAndSearch];
    if (showOtherAnimals) {
      salonsToFilter = salonsToFilter.filter(salon => (salon.extra || '').trim().startsWith('1'));
    }
    return salonsToFilter.sort((a, b) => a.name.localeCompare(b.name));
  }, [salonsAfterAreaAndSearch, showOtherAnimals]);
  
  const shouldGlowSwitch = showOtherAnimals && salonsAfterAreaAndSearch.length > 0 && filteredSalons.length === 0;

  const noResultsForSearchTerm = searchTerm && filteredSalons.length === 0;

  const itemsToDisplay = noResultsForSearchTerm ? salons.filter(salon => {
      // When showing all results, still respect the "other animals" toggle
      if (showOtherAnimals) {
        return (salon.extra || '').trim().startsWith('1');
      }
      return true;
    }) : filteredSalons;

  const itemsWithAdsAndTips = useMemo(() => {
    const salonItems: SalonWithDistance[] = [...itemsToDisplay];
    if (salonItems.length === 0) {
        return [];
    }

    const interleavedItems: (SalonWithDistance | { type: string, id: string, html?: string, salon?: Salon, tip?: string, content?: React.ReactNode })[] = [];
    
    const availableInfoBlocks = [...shuffledInfoBlocks];
    const availableTips = [...shuffledTips];
    const isDistrictPage = currentArea && currentArea !== 'Alle';

    let lastAdIndex = -1; // Keep track of the last used ad index.

    salonItems.forEach((salon, index) => {
      const salonNumber = index + 1;
      
      interleavedItems.push(salon);

      // --- Smart Insertion Logic ---

      // Ad every 4th item, with random ad selection, avoiding consecutive duplicates.
      if (salonNumber > 0 && salonNumber % 4 === 0) {
        const availableAds = daisyconAds.salonListAds;
        if (availableAds.length > 0) {
            let randomIndex = Math.floor(Math.random() * availableAds.length);
            // If there's more than one ad, ensure the new one isn't the same as the last one.
            if (availableAds.length > 1) {
                while (randomIndex === lastAdIndex) {
                    randomIndex = Math.floor(Math.random() * availableAds.length);
                }
            }
            lastAdIndex = randomIndex; // Update the last used index.
            const adHtml = availableAds[randomIndex];
            interleavedItems.push({ type: 'daisycon-ad', id: `daisycon-ad-${salonNumber}`, html: adHtml });
        }
      }
      // INFO BLOCKS: After every 7th salon.
      else if (salonNumber > 0 && salonNumber % 7 === 0) {
        if (availableInfoBlocks.length > 0) {
          const blockToInsert = availableInfoBlocks.shift();
          if (blockToInsert) {
            interleavedItems.push({ ...blockToInsert, id: `${blockToInsert.id}-${salonNumber}` });
          }
        }
      }
      // TIPS: After salon 2 and then every 5th slot after that.
      else if (salonNumber === 2 || (salonNumber > 2 && (salonNumber - 2) % 5 === 0)) {
        if (availableTips.length > 0) {
          const tipToInsert = availableTips.shift();
          if (tipToInsert) {
             interleavedItems.push({ type: 'tip', id: `tip-${salonNumber}`, tip: tipToInsert });
          }
        }
      }
      
      // PERSONAL AD: After salon 2 on province pages. Can appear with a tip.
      if (!isDistrictPage && salonNumber === 2) {
        const featuredSalon = itemsToDisplay.find(s => 'pagePicture' in s && s.pagePicture);
        if (featuredSalon) {
          interleavedItems.push({ type: 'personal-ad', id: `personal-ad-${salonNumber}`, salon: featuredSalon as Salon });
        }
      }
    });

    const hasAd = interleavedItems.some(item => 'type' in item && item.type === 'daisycon-ad');
    // For short lists, insert one random ad.
    if (salonItems.length > 0 && salonItems.length < 5 && !hasAd) {
        const availableAds = daisyconAds.salonListAds;
        if (availableAds.length > 0) {
            const randomIndex = Math.floor(Math.random() * availableAds.length);
            const adHtml = availableAds[randomIndex];
            interleavedItems.splice(1, 0, { type: 'daisycon-ad', id: 'daisycon-ad-single', html: adHtml });
        }
    }

    return interleavedItems;
  }, [itemsToDisplay, currentArea, shuffledTips, shuffledInfoBlocks]);


  const heroImageSrc = placeholderImages.heroImage?.src ? `/${placeholderImages.heroImage.src}` : "https://picsum.photos/seed/groomer-closeup/1200/600";
  const heroImageHint = placeholderImages.heroImage?.hint || "dog grooming professional";

  const getLinkWithParams = (baseHref: string) => {
    const params = new URLSearchParams(searchParams);
    const queryString = params.toString();
    return queryString ? `${baseHref}?${queryString}` : baseHref;
  };

  const renderPillButtons = () => {
    if (!allAreas || !provinceSlug) {
      return null;
    }
  
    const pills = [
      { area: 'Alle', isTop: true, link: `/hondenkapsalons-in-${provinceSlug}` },
      ...topAreas.map(area => ({ area, isTop: true, link: `/hondenkapsalons-in-${provinceSlug}/${slugify(area)}` })),
      ...(otherAreas.length > 0 ? [{ area: 'Meer...', isTop: false, isDropdown: true, link: '#' }] : [])
    ];
  
    let effectiveArea = currentArea;
    if (pendingAreaSlug === 'Alle' && searchTerm) {
      effectiveArea = 'Alle';
    } else if (pendingAreaSlug && pendingAreaSlug !== 'Alle') {
        const areaFromSlug = allAreas.find(a => slugify(a) === pendingAreaSlug);
        if (areaFromSlug) effectiveArea = areaFromSlug;
    }
  
    const isOtherAreaSelected = effectiveArea && otherAreas.includes(effectiveArea);
  
    const chunk = <T,>(arr: T[], size: number): T[][] =>
      Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
      );
  
    const otherAreasColumns = chunk(otherAreas, 10);
  
    return pills.map((pill, index) => {
      const buttonContent = (
          <>
              {pill.area}
              {pill.isDropdown && <ChevronDown className="ml-2 h-4 w-4 inline" />}
          </>
      );
  
      if (pill.isDropdown) {
        return (
          <DropdownMenu key="more-dropdown">
            <DropdownMenuTrigger asChild>
              <Button
                variant={isOtherAreaSelected ? 'default' : 'secondary'}
                className={`rounded-full shrink-0 opacity-0 animate-fade-in ${!isOtherAreaSelected ? 'bg-white/90 text-foreground hover:bg-white' : ''}`}
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
              >
                  {isOtherAreaSelected ? effectiveArea : 'Meer...'}
                  <ChevronDown className="ml-2 h-4 w-4 inline" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="p-0 max-w-[90vw] relative">
                <div className="overflow-x-auto overflow-y-hidden no-scrollbar p-2">
                  <div className="flex gap-4">
                      {otherAreasColumns.map((column, colIndex) => (
                          <div key={colIndex} className="flex flex-col flex-shrink-0">
                              {column.map(area => (
                                  <DropdownMenuItem key={area} asChild className="whitespace-nowrap">
                                      <Link 
                                          href={getLinkWithParams(`/hondenkapsalons-in-${provinceSlug}/${slugify(area)}`)}
                                          onClick={() => setPendingAreaSlug(slugify(area))}
                                      >
                                          {area}
                                      </Link>
                                  </DropdownMenuItem>
                              ))}
                          </div>
                      ))}
                  </div>
                </div>
                 <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent" />
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
  
      const pillSlug = pill.area === 'Alle' ? 'Alle' : slugify(pill.area);
      const isActive = (!searchTerm && ((effectiveArea === 'Alle' && pill.area === 'Alle') || (effectiveArea && slugify(effectiveArea) === pillSlug)));
  
      return (
        <Button
          id={pill.area === 'Alle' ? 'pill-button-alle' : undefined}
          key={pill.area}
          asChild
          variant={isActive ? 'default' : 'secondary'}
          className={`rounded-full shrink-0 opacity-0 animate-fade-in ${!isActive ? 'bg-white/90 text-foreground hover:bg-white' : ''}`}
          style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
          onClick={() => { setPendingAreaSlug(pillSlug); setSearchTerm(''); }}
        >
          <Link href={getLinkWithParams(pill.link)}>
             {pill.area}
          </Link>
        </Button>
      );
    });
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    (document.activeElement as HTMLElement)?.blur();
  };

  let displayArea = currentArea;
  if (searchTerm) {
    displayArea = 'Alle';
  }

  const handleRetry = async () => {
    if (!provinceName) {
        toast({
            variant: 'destructive',
            title: 'Fout',
            description: 'Kon provincie niet bepalen om opnieuw te laden.',
        });
        return;
    }

    setIsRetrying(true);
    try {
        const newSalons = await revalidateSalons(provinceName);
        setSalons(newSalons);

        if (newSalons.length > 0) {
            toast({
                title: 'Succes!',
                description: 'De lijst met salons is bijgewerkt.',
            });
        } else {
            toast({
                variant: 'destructive',
                title: 'Laden mislukt',
                description: 'Kon de gegevens nog steeds niet ophalen. Probeer het later opnieuw.',
            });
        }
    } catch (error) {
        toast({
            variant: 'destructive',
            title: 'Er is een fout opgetreden',
            description: 'Kon de gegevens niet opnieuw laden.',
        });
    } finally {
        setIsRetrying(false);
    }
  };

  return (
    <>
      <div className="bg-background">
        <div className="relative w-full h-[420px] bg-muted">
          <Image
            src={heroImageSrc}
            alt="Hond wordt getrimd"
            fill
            className="object-cover"
            data-ai-hint={heroImageHint}
            priority
          />
          <div className="absolute inset-0 bg-slate-800/70 p-4 flex flex-col">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-sm font-bold text-white/80 uppercase tracking-wider">{displayArea && displayArea !== 'Alle' ? `Provincie ${provinceName} / ${displayArea}` : `Provincie ${provinceName}`}</span>
                <div>
                    <Link href="/hondenkapsalons" className="text-3xl font-extrabold leading-tight tracking-tight text-white hover:underline">Hondenkapsalons</Link>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <form
                  onSubmit={handleSearchSubmit}
                  className="group relative w-full rounded-xl bg-white/90 transition-colors focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 dark:focus-within:ring-offset-background"
                >
                    <div className="absolute left-0 top-0 bottom-0 text-muted-foreground flex items-center justify-center pl-4 pr-2">
                        <Search className="w-5 h-5" />
                    </div>
                    <Input 
                      className="w-full bg-transparent border-none focus:ring-0 placeholder:text-muted-foreground pl-12 pr-28 text-base font-medium h-12 focus-visible:ring-transparent focus-visible:ring-offset-0 text-foreground" 
                      placeholder="Zoek op naam, gemeente of specialisatie..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button
                      type="button"
                      onClick={handleLocate}
                      disabled={isLocating}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-10 shrink-0 bg-primary/20 text-foreground hover:bg-primary/30 px-3"
                      variant="ghost"
                      size="sm"
                    >
                      {isLocating ? <Loader className="animate-spin" /> : 'In de buurt'}
                    </Button>
                </form>
              <div className="flex items-center space-x-2 rounded-lg p-2 -m-2 transition-all">
                  <Switch 
                    id="other-animals" 
                    checked={showOtherAnimals} 
                    onCheckedChange={handleShowOtherAnimalsChange} 
                    className={cn(shouldGlowSwitch && "animate-pulse-glow")}
                  />
                  <Label htmlFor="other-animals" className="text-white font-medium flex items-center gap-2 cursor-pointer">
                      Toon enkel salons die ook katten/andere dieren trimmen
                  </Label>
              </div>
              <div className="flex flex-wrap gap-3 items-center min-h-[52px]">
                {renderPillButtons()}
              </div>
            </div>

          </div>
        </div>
        
        <main className="w-full max-w-4xl mx-auto flex flex-col gap-4 pt-4 px-4 pb-20">
            {displayArea === 'Alle' && !searchTerm && (
                <div className="p-4 bg-muted/30">
                    <Card className="shadow-none border-0 bg-transparent">
                        <CardContent className="p-0">
                            <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/80 text-sm">
                                <h2 className="text-xl font-semibold text-foreground">Vind het Beste Hondenkapsalon in Provincie {provinceName}</h2>
                                <p>
                                    Welkom op kzoek.com. Een bezoek aan een hondentrimsalon is voor veel eigenaars een terugkerend onderdeel van de verzorging, maar roept vaak ook vragen op: Wanneer is trimmen nodig? Wat gebeurt er precies tijdens een trimbeurt? En hoe kiest u het juiste salon voor uw hond?
                                </p>
                                <p>
                                    Deze website helpt hondeneigenaars in België, met een focus op de provincie {provinceName}, om weloverwogen keuzes te maken. Of u nu in het centrum woont of in een rustige wijk, wij helpen u de ideale trimmer voor uw trouwe viervoeter te vinden. Gebruik de zoekfunctie of filter op gemeente om snel en eenvoudig een professioneel hondenkapsalon, hondenkapper of dierenverzorgingssalon bij u in de buurt te ontdekken.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
          <div className="flex justify-between items-center px-1">
            <span className="text-sm text-muted-foreground">
              {noResultsForSearchTerm
                ? `${itemsToDisplay.length} resultaten worden weergegeven`
                : `${itemsToDisplay.length} ${itemsToDisplay.length === 1 ? 'resultaat' : 'resultaten'} gevonden`
              }
              {displayArea && displayArea !== 'Alle' && !searchTerm && ` in ${displayArea}`}
            </span>
             <Button asChild size="sm" className="bg-primary/20 text-foreground hover:bg-primary/30">
                <Link href="/blog?category=Hondenkapsalons">
                    <Lightbulb className="mr-2 h-4 w-4" />
                    Tips & Artikels
                </Link>
            </Button>
          </div>
          {noResultsForSearchTerm && (
            <div className="text-center py-4 bg-yellow-100/50 text-yellow-800 rounded-lg">
                <p>Geen salons gevonden voor "{searchTerm}". Hieronder vindt u de volledige lijst.</p>
            </div>
          )}
          {initialSalons.length === 0 && salons.length === 0 ? (
            <div className="text-center py-10 bg-card border rounded-lg">
                <WifiOff className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold">Laden van gegevens mislukt</h3>
                <p className="text-muted-foreground mt-2 mb-6 max-w-md mx-auto">We konden de lijst met salons niet ophalen. Dit kan een tijdelijk probleem zijn met de dataverbinding.</p>
                <Button onClick={handleRetry} disabled={isRetrying}>
                    {isRetrying && <Loader className="mr-2 animate-spin" />}
                    {isRetrying ? 'Opnieuw proberen...' : 'Probeer opnieuw'}
                </Button>
            </div>
          ) : itemsWithAdsAndTips.length > 0 ? (
            <div className="flex flex-col gap-4">
              {itemsWithAdsAndTips.map((item, index) => {
                 if ('type' in item && item.type === 'daisycon-ad' && item.html) {
                    return <DaisyconAd key={item.id} html={item.html} />;
                 }
                if ('type' in item && item.type === 'personal-ad' && item.salon) {
                  if (item.salon.pagePicture) {
                    return <PersonalAd key={item.id} featuredSalon={item.salon} />;
                  }
                  return null;
                }
                if ('type' in item && item.type === 'tip' && item.tip) {
                    return <TipCard key={item.id} tip={item.tip} />;
                }
                if ('type' in item && item.type === 'info-block' && item.content) {
                    return <InfoBlockCard key={item.id} content={item.content} />;
                }

                const salon = item as SalonWithDistance;
                const addressParts = salon.address.split(',');
                const addressLocality = addressParts.length > 1 ? addressParts[addressParts.length - 1].trim() : '';
                const showArea = salon.area && !normalizeText(addressLocality).includes(normalizeText(salon.area));
                
                const stats = reviewStats[salon.id];

                return (
                  <Card key={salon.id} className="hover:border-primary transition-colors flex flex-col overflow-hidden h-full group">
                    <Link href={getLinkWithParams(`/hondenkapsalons-in-${provinceSlug}/${slugify(salon.area)}/${salon.slug}`)} className="block p-4 flex-grow text-center flex flex-col justify-between">
                        <div>
                          <div className="flex justify-center items-center flex-wrap gap-2 mb-1">
                            <CardTitle className="group-hover:text-primary transition-colors">{salon.name}</CardTitle>
                             {stats && stats.count > 0 && (
                                <div title={`${stats.score.toFixed(1)} van 10 gemiddeld, gebaseerd op ${stats.count} reviews`} className="flex-shrink-0 flex items-center justify-center gap-1 text-sm font-bold text-amber-600">
                                    <Star className="w-4 h-4 fill-current text-amber-500" />
                                    <span>{stats.score.toFixed(1)}</span>
                                    <span className="text-xs font-normal text-muted-foreground">({stats.count})</span>
                                </div>
                            )}
                          </div>
                            {showArea && <div className="text-sm text-muted-foreground">{salon.area}</div>}
                            <p className="text-muted-foreground text-sm mt-1">{salon.address}</p>
                        </div>
                        
                        <div className={buttonVariants({ variant: 'default', className: "inline-flex items-center justify-center gap-2 mt-4 text-sm font-medium h-9 px-3 rounded-md group-hover:bg-primary/90 transition-colors" })}>
                            <span>Bekijk details</span>
                            <ArrowRight className="h-4 w-4 transform transition-transform group-hover:translate-x-1" />
                        </div>
                      </Link>
                      {isClient && (salon.openingHours || salon.parking) && (
                        <div className="border-t p-3 bg-secondary/50 flex justify-center items-center gap-2">
                          {salon.openingHours && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="inline-flex items-center rounded-full border border-transparent bg-background text-foreground hover:bg-accent px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer flex gap-1.5">
                                    <Clock className="h-3 w-3" />
                                    Openingsuren
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto max-w-[250px] sm:max-w-xs text-sm whitespace-pre-wrap">
                                  {salon.openingHours}
                              </PopoverContent>
                            </Popover>
                          )}
                          {salon.parking && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="inline-flex items-center rounded-full border border-transparent bg-background text-foreground hover:bg-accent px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer flex gap-1.5">
                                    <ParkingCircle className="h-3 w-3" />
                                    Parking
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto max-w-[250px] sm:max-w-xs text-sm whitespace-pre-wrap">
                                  {salon.parking}
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      )}
                    </Card>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-10 bg-card border rounded-lg">
                <Search className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold">Geen salons gevonden</h3>
                <p className="text-muted-foreground mt-2">Er zijn geen salons die overeenkomen met uw huidige selectie.</p>
            </div>
          )}
        </main>
      </div>
      <Button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        variant="ghost"
        className={cn(
          'fixed bottom-6 right-3 z-50 h-14 w-10 rounded-xl bg-primary/20 text-primary-foreground/80 shadow-lg hover:bg-primary/30 hover:text-primary-foreground',
          'transition-opacity duration-300 ease-in-out',
          showScrollTopButton ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        size="icon"
        tabIndex={showScrollTopButton ? 0 : -1}
        aria-hidden={!showScrollTopButton}
      >
        <ArrowUp className="h-6 w-6" />
        <span className="sr-only">Scroll naar boven</span>
      </Button>
    </>
  );
}
